import { useCallback, useMemo } from "react";
import { FetchCallBackResult, FetchFn, InitialState, useDataFetch } from "@inception/ui";
import { AlertFilterResponse } from "../types";
import { operationaliseV2ApiService } from "../OperationaliseV2Api";

export const useFetchIncidentsData = (
  opId: string,
  startTimeMillis: number,
  endTimeMillis: number,
  lazyFetch = false,
  includeSnapshots = false,
  includeLookupData = false,
  includeAdditionalData = false,
  includeActions = false,
  generateDemoData = false
) => {
  const initState = useMemo<InitialState<AlertFilterResponse, string>>(
    () => ({
      data: null,
      error: null,
      isError: false,
      isFetching: !lazyFetch,
      isSuccess: false
    }),
    [lazyFetch]
  );

  const fetchFn = useCallback<FetchFn<AlertFilterResponse, string>>(async () => {
    const resData: FetchCallBackResult<AlertFilterResponse, string> = {
      data: null,
      error: null,
      isError: false,
      isSuccess: false
    };

    try {
      const { data, error, message } = await operationaliseV2ApiService.getIncidentsData(
        opId,
        startTimeMillis,
        endTimeMillis,
        includeSnapshots,
        includeLookupData,
        includeAdditionalData,
        includeActions,
        generateDemoData
      );

      if (error) {
        resData.error = typeof message !== "string" ? "Error fetching incidents" : message;
        resData.isError = true;
      } else {
        resData.isSuccess = true;
        resData.data = data;
      }
    } catch (err) {
      resData.error = err.toString();
      resData.isError = true;
    }

    return resData;
  }, [
    endTimeMillis,
    generateDemoData,
    includeActions,
    includeAdditionalData,
    includeLookupData,
    includeSnapshots,
    opId,
    startTimeMillis
  ]);

  return useDataFetch(fetchFn, initState, lazyFetch);
};
